import { PhoneIcon } from "@heroicons/react/24/outline";
import {
    AreaCode,
    chooseOfficeToReservePhoneNumberMessage,
    parseAreaCode,
    RegionId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { ClientGraphqlError } from "backend/fetcher";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    ListRentablePhoneNumbersQuery,
    RentablePhoneNumber,
    useClaimPhoneNumberMutation,
    useListRentablePhoneNumbersQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";

interface CreateTelecomPhoneLineProps {
    onCreated: (phoneNumber: RentablePhoneNumber) => void;
}

export const CreateTelecomPhoneLine: React.FC<CreateTelecomPhoneLineProps> = props => {
    const regionId = useRegionId();
    return regionId ? (
        <CreateTelecomPhoneLineInOffice regionId={regionId} onCreated={props.onCreated} />
    ) : (
        <InlineBanner type="info">{chooseOfficeToReservePhoneNumberMessage}</InlineBanner>
    );
};

const useNewNumberBusinessName = (regionId: RegionId): string | null => {
    const isBusinessVerificationEnabled = useFeature("businessVerification");
    const trpcClient = trpc.useTRPC();
    const businessQuery = useQuery({
        ...trpcClient.businessProfile.newNumberBusiness.queryOptions({ regionId }),
        enabled: isBusinessVerificationEnabled,
    });
    return businessQuery.data?.name ?? null;
};

const CreateTelecomPhoneLineInOffice: React.FC<
    CreateTelecomPhoneLineProps & { regionId: RegionId }
> = props => {
    const { onCreated, regionId } = props;
    const businessName = useNewNumberBusinessName(regionId);
    const [rawAreaCode, setRawAreaCode] = useState<string | null>(null);

    const areaCode: AreaCode | null = useMemo(() => {
        if (!rawAreaCode) {
            return null;
        }
        return parseAreaCode(rawAreaCode).getOrElse(null);
    }, [rawAreaCode]);

    const getRentablePhoneNumbers = useListRentablePhoneNumbersQuery(
        {
            request: {
                areaCode,
            },
        },
        {
            enabled: !!areaCode,
        },
    );

    const handleAreaCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = e.target.value.replace(/\D/g, "");
        setRawAreaCode(digitsOnly);
    }, []);

    const handleSuggestionClick = useCallback((suggested: AreaCode) => {
        setRawAreaCode(suggested);
    }, []);

    const handlePhoneNumberClaimed = useCallback(
        (phoneNumber: RentablePhoneNumber) => {
            onCreated(phoneNumber);
        },
        [onCreated],
    );

    return (
        <div className="space-y-4">
            {businessName ? (
                <InlineBanner type="info">
                    {registeredUnderText(businessName)}
                </InlineBanner>
            ) : null}
            <Input
                label="Area Code"
                inputMode="numeric"
                pattern="\d{3}"
                value={rawAreaCode}
                onChange={handleAreaCodeChange}
                maxLength={3}
            />
            {areaCode ? (
                <QueryRenderer
                    name="CreateTelecomPhoneLine.getRentablePhoneNumbers"
                    query={getRentablePhoneNumbers}>
                    {data => (
                        <RentablePhoneNumberList
                            response={data}
                            areaCode={areaCode}
                            regionId={regionId}
                            businessName={businessName}
                            onClaimed={handlePhoneNumberClaimed}
                            onSuggestionClick={handleSuggestionClick}
                        />
                    )}
                </QueryRenderer>
            ) : (
                <InlineBanner type="info">
                    Enter a US area code to see available phone numbers
                </InlineBanner>
            )}
        </div>
    );
};

const registeredUnderText = (businessName: string): string =>
    `This number will be registered under ${businessName}.`;

interface RentablePhoneNumberListProps {
    response: ListRentablePhoneNumbersQuery;
    areaCode: AreaCode;
    regionId: RegionId;
    businessName: string | null;
    onClaimed: (phoneNumber: RentablePhoneNumber) => void;
    onSuggestionClick: (areaCode: AreaCode) => void;
}

const RentablePhoneNumberList: React.FC<RentablePhoneNumberListProps> = props => {
    const { response, areaCode, regionId, businessName, onClaimed, onSuggestionClick } =
        props;

    const [claiming, setClaiming] = useState<RentablePhoneNumber | null>(null);

    const [claimError, setClaimError] = useState<string | null>(null);

    const handleClaiming = useCallback((phoneNumber: RentablePhoneNumber) => {
        setClaimError(null);
        setClaiming(phoneNumber);
    }, []);

    const handleClaimed = useCallback(
        (phoneNumber: RentablePhoneNumber) => {
            setClaiming(null);
            onClaimed(phoneNumber);
        },
        [onClaimed],
    );

    const handleClaimFailed = useCallback((message: string) => {
        setClaiming(null);
        setClaimError(message);
    }, []);

    if (!response) {
        return null;
    }

    if (response.listRentablePhoneNumbers.phoneNumbers.length === 0) {
        const suggestions = response.listRentablePhoneNumbers.suggestedAreaCodes ?? [];
        if (suggestions.length === 0) {
            return (
                <InlineBanner type="info">
                    No phone numbers available for area code &quot;{areaCode}&quot;
                </InlineBanner>
            );
        }
        const metroName = suggestions[0].metroName;
        return (
            <InlineBanner type="info">
                <div className="space-y-2">
                    <div>
                        No phone numbers available for area code &quot;{areaCode}
                        &quot;
                        {metroName ? ` (${metroName})` : ""}. Try a nearby area code:
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.map(suggestion => (
                            <SecondaryCompactButton
                                key={suggestion.areaCode}
                                onClick={() => onSuggestionClick(suggestion.areaCode)}>
                                {suggestion.areaCode}
                            </SecondaryCompactButton>
                        ))}
                    </div>
                </div>
            </InlineBanner>
        );
    }

    return (
        <div className="space-y-4">
            {claimError ? <InlineBanner type="error">{claimError}</InlineBanner> : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {response.listRentablePhoneNumbers.phoneNumbers.map(number => (
                    <RentablePhoneNumberCard
                        key={number.phoneNumber}
                        phoneNumber={number}
                        regionId={regionId}
                        businessName={businessName}
                        disabled={!!claiming}
                        onClaiming={handleClaiming}
                        onClaimed={handleClaimed}
                        onClaimFailed={handleClaimFailed}
                    />
                ))}
            </div>
        </div>
    );
};

const claimFailureFallback = "We couldn't reserve this number. Please try again.";

const claimFailureMessage = (error: unknown): string =>
    error instanceof ClientGraphqlError
        ? (error.messageForErrorTypes(["INVALID_RECORD"]) ?? claimFailureFallback)
        : claimFailureFallback;

interface RentablePhoneNumberCardProps {
    phoneNumber: RentablePhoneNumber;
    regionId: RegionId;
    businessName: string | null;
    disabled: boolean;
    onClaiming: (phoneNumber: RentablePhoneNumber) => void;
    onClaimed: (phoneNumber: RentablePhoneNumber) => void;
    onClaimFailed: (message: string) => void;
}

const RentablePhoneNumberCard: React.FC<RentablePhoneNumberCardProps> = props => {
    const {
        phoneNumber,
        regionId,
        businessName,
        onClaimed,
        disabled,
        onClaiming,
        onClaimFailed,
    } = props;

    const claimPhoneNumber = useClaimPhoneNumberMutation();

    const handleClaimPhoneNumber = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (disabled) {
                return;
            }

            const confirmed = await fancyConfirm(
                `Claim ${phoneNumber.friendlyName}?`,
                businessName
                    ? registeredUnderText(businessName)
                    : "Are you sure you want to reserve this number?",
                "Yes, reserve it",
                "No, cancel",
            );

            if (!confirmed) {
                return;
            }
            onClaiming(phoneNumber);
            try {
                await claimPhoneNumber.mutateAsync({
                    request: {
                        phoneNumber: phoneNumber.phoneNumber,
                        regionId,
                    },
                });
            } catch (error: unknown) {
                onClaimFailed(claimFailureMessage(error));
                return;
            }
            onClaimed(phoneNumber);
        },
        [
            onClaimed,
            phoneNumber,
            regionId,
            businessName,
            claimPhoneNumber,
            disabled,
            onClaiming,
            onClaimFailed,
        ],
    );

    return (
        <div className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow">
            <div className="flex space-x-6 w-full items-start justify-between p-6">
                <div className="flex-1 overflow-auto">
                    <div className="flex space-x-2">
                        <div className="flex-1">
                            <h3 className="text-gray-900 text-lg">
                                <div className="flex space-x-2 items-center">
                                    <div className="w-6">
                                        <PhoneIcon className="text-gray-500 w-6 h-6" />
                                    </div>
                                    <div>{phoneNumber.friendlyName}</div>
                                </div>
                            </h3>
                            <div className="text-gray-500">
                                <div className="mt-2">
                                    <div className="flex items-center space-x-1">
                                        <div>
                                            {phoneNumber.friendlyLocation || (
                                                <span className="text-gray-400">
                                                    No location
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={handleClaimPhoneNumber}
                        className="relative inline-flex w-0 flex-1 items-center justify-center rounded-bl-lg border border-transparent py-4 text-sm font-medium text-brand-600 hover:bg-brand-100 hover:text-brand-800 twoverride">
                        <span className="ml-3">
                            {claimPhoneNumber.isLoading ? <Spinner /> : "Reserve"}
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );
};
